import { wccModules } from 'enums/wccModules';
import { CustomElementConfig } from 'extensions/knockout/bindings/customElement';
import { AttachmentsHelpers } from 'helpers/attachments';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import WCCUpload from 'models/upload';
import { WCCAttachmentControlsVMConfig } from '../decorators/controls/controls';
import { WCCAttachmentLayoutVMTheme } from '../layouts/base/base.interfaces';
import { IAttachmentLayoutFactory, WCCAttachmentLayout } from '../layouts/iFactory';
import './upload.html';
import './upload.scss';

const { allowedUploadImageExtensions, allowedUploadImageExtensionsForTopicThreads, allowedUploadVideoExtensions } = settings;

components.preload('attachment-controls', 'wcc-image');

export interface WCCUploadVMConfig {
    upload: SubscribableOrNullableValue<WCCUpload>
    layout?: SubscribableOrNullableValue<WCCAttachmentLayout>
    theme?: SubscribableOrNullableValue<WCCAttachmentLayoutVMTheme>
    cover: SubscribableOrNullableValue<boolean | number>
}

@injectable()
export default class WCCUploadVM {
    private upload: Subscribable<WCCUpload | undefined>

    name: Subscribable<string>
    progress: Subscribable<number>

    preview: Subscribable<string | undefined>
    icon: Subscribable<string>

    layoutConfig: Subscribable<CustomElementConfig>
    controlsConfig: Subscribable<WCCAttachmentControlsVMConfig | undefined>

    constructor(
        @inject(wccModules.componentConfig) config: WCCUploadVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.attachmentLayoutFactory) layoutFactory: IAttachmentLayoutFactory
    ) {
        this.upload = ko.flattenComputed(config.upload);

        const coverConfig = ko.flattenComputed(config.cover);

        this.name = this.upload.pluck('name', '');
        this.progress = this.upload.pluck('progress', 0);
        this.preview = this.upload.pluck('preview');

        const extension = ko.pureComputed(() => system.getExtension(this.name()));
        const isImage = extension.mapNotNull(extension => allowedUploadImageExtensions.includes(extension) || allowedUploadImageExtensionsForTopicThreads.includes(extension)).default(false);
        const isVideo = extension.mapNotNull(extension => allowedUploadVideoExtensions.includes(extension)).default(false);

        this.icon = ko.strictComputed((extension, isImage, isVideo): string => {
            if (extension != undefined) {
                if (isImage)
                    return 'fal fa-image';
                else if (isVideo)
                    return 'fal fa-film';
            }

            return AttachmentsHelpers.getFileIcon(extension);
        }, [extension, isImage, isVideo]);

        const ratio = ko.strictComputed(config => {
            if (_.isNumber(config))
                return config;

            return 1.77;
        }, [coverConfig]);

        this.layoutConfig = layoutFactory.create({
            layout: config.layout,

            config: {
                ratio: ratio,
                theme: config.theme
            }
        });

        this.controlsConfig = this.upload.pluck<WCCAttachmentControlsVMConfig>(upload => ({
            progress: {
                value: this.progress,
                icon: this.icon
            },

            expand: {
                src: this.preview,
                title: this.name
            },

            cancel: upload.isCancellable() ? () => upload.cancel() : undefined
        }));

        ko.applyBindingsToNode(info.element, {
            'css': {
                'wcc-upload': true
            },

            'style': {
                'background-image': this.preview.pluck(preview => `url(${preview})`)
            }
        });
    }
}